// --- Request types ---

export interface UtmParams {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
}

export type CreateSessionRequest = UtmParams;

export interface ResumeSessionRequest {
  resumeToken: string;
}

// --- Response types ---

export type CpqStep =
  | 'STEP1_ADDR_CHECK'
  | 'STEP2_LANDING'
  | 'STEP3_SELECT'
  | 'STEP4_CONFIG'
  | 'STEP5_CART'
  | 'STEP6_REVIEW'
  | 'STEP7_SUBMIT'
  | 'DONE';

export type SessionStatus = 'IN_PROGRESS' | 'COMPLETED' | 'EXPIRED';

export interface CreateSessionResponse {
  sessionId: string;
  resumeToken: string;
  currentStep: string;
  expiresAt: string;
}

export interface SessionUi {
  tenantId: string;
  currentStep: CpqStep;
  status: SessionStatus;
  customerId: string;
  expiresAt: string;
  suggestedRoute: string;
}

export interface ResumeSessionResponse {
  sessionId: string;
  currentStep: CpqStep;
  requiredRtypes: string[];
  resources: Record<string, unknown>;
  ui: SessionUi;
}

// --- LocalStorage keys (used by SessionContext → useLocalStorage) ---

export const CPQ_SESSION_ID_KEY = 'cpq_session_id';
export const CPQ_RESUME_TOKEN_KEY = 'cpq_resume_token';
export const CPQ_SESSION_STORED_AT_KEY = 'cpq_session_stored_at';
export const CPQ_QUALIFICATION_CACHE_KEY = 'cpq_qualification_cache';
export const CPQ_QUALIFICATION_FLOW_KEY = 'cpq_qualification_flow';
export const CPQ_CHECKOUT_STATE_KEY = 'cpq_checkout_state';
export const CPQ_ORDER_DATA_KEY = 'cpq_order_data';
export const CPQ_GTM_FIRED_EVENTS_KEY = 'cpq_gtm_fired_events';

/** Session TTL: 7 days in milliseconds */
export const SESSION_TTL_MS = 7 * 24 * 60 * 60 * 1000;

// --- Service abstraction (Dependency Inversion) ---

export interface SessionService {
  createSession: (payload?: CreateSessionRequest) => Promise<CreateSessionResponse>;
  getSessionStatus: (sessionId: string) => Promise<SessionStatusResponse>;
}

export interface SessionStatusResponse {
  sessionId: string;
  currentStep: string;
  status: string;
}

// --- Context types ---

export interface StoredSession {
  sessionId: string;
  resumeToken: string;
}

export interface SessionContextValue {
  sessionId: string | null;
  resumeToken: string | null;
  currentStep: string | null;
  expiresAt: string | null;
  /** True while we are validating a stored session on startup */
  isInitializing: boolean;
  /** Whether a valid (non-expired, active) session exists */
  hasValidSession: boolean;
  setSession: (session: CreateSessionResponse) => void;
  updateFromResume: (session: ResumeSessionResponse) => void;
  clearSession: () => void;
  /**
   * Ensures a valid session exists: reuses stored session if valid,
   * otherwise creates a new one. Returns the sessionId.
   */
  ensureSession: () => Promise<string>;
}
